(() => {
  const state = {
    ready: false,
    analyticsAllowed: false,
    marketingAllowed: false,
    preferencesAllowed: false,
  };

  const listeners = new Set();

  const getConsentApi = () => {
    if (!window.Shopify || !window.Shopify.customerPrivacy) return null;
    return window.Shopify.customerPrivacy;
  };

  const safeBoolean = (fn) => {
    try {
      return Boolean(fn());
    } catch (error) {
      return false;
    }
  };

  const detail = () => ({
    ready: state.ready,
    analyticsAllowed: state.analyticsAllowed,
    marketingAllowed: state.marketingAllowed,
    preferencesAllowed: state.preferencesAllowed,
  });

  const canRunConsentType = (consentType) => {
    const normalized = (consentType || '').toLowerCase();
    if (normalized === 'marketing') return state.marketingAllowed;
    if (normalized === 'preferences') return state.preferencesAllowed;
    if (normalized === 'analytics') return state.analyticsAllowed;
    return false;
  };

  const activateDeferredConsentScripts = () => {
    const scripts = document.querySelectorAll('script[type="text/plain"][data-ea-consent]:not([data-ea-consent-loaded="true"])');

    scripts.forEach((script) => {
      if (!canRunConsentType(script.dataset.eaConsent)) return;

      const activeScript = document.createElement('script');
      for (const { name, value } of [...script.attributes]) {
        if (name === 'type' || name === 'data-ea-consent' || name === 'data-ea-consent-loaded') continue;
        activeScript.setAttribute(name, value);
      }

      if (script.src) {
        activeScript.src = script.src;
      } else {
        activeScript.text = script.textContent || '';
      }

      script.dataset.eaConsentLoaded = 'true';
      script.insertAdjacentElement('afterend', activeScript);
    });
  };

  const broadcast = (source) => {
    const payload = { source, ...detail() };

    listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        // no-op: observer callback failures should not break consent updates
      }
    });

    document.dispatchEvent(new CustomEvent('ea:consent-state-changed', { detail: payload }));
  };

  const refreshFromApi = (source = 'refresh') => {
    const consentApi = getConsentApi();
    if (!consentApi) return false;

    state.analyticsAllowed = safeBoolean(() => consentApi.analyticsProcessingAllowed());
    state.marketingAllowed = safeBoolean(() => consentApi.marketingAllowed());
    state.preferencesAllowed = safeBoolean(() => consentApi.preferencesProcessingAllowed());
    state.ready = true;

    activateDeferredConsentScripts();
    broadcast(source);
    return true;
  };

  const registerHelpers = () => {
    window.analyticsAllowed = () => {
      const consentApi = getConsentApi();
      return consentApi ? safeBoolean(() => consentApi.analyticsProcessingAllowed()) : false;
    };

    window.marketingAllowed = () => {
      const consentApi = getConsentApi();
      return consentApi ? safeBoolean(() => consentApi.marketingAllowed()) : false;
    };

    window.preferencesAllowed = () => {
      const consentApi = getConsentApi();
      return consentApi ? safeBoolean(() => consentApi.preferencesProcessingAllowed()) : false;
    };

    window.EAConsentGate = {
      refresh: () => refreshFromApi('manual-refresh'),
      getState: () => detail(),
      onChange: (listener) => {
        if (typeof listener !== 'function') return () => {};
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      analyticsAllowed: window.analyticsAllowed,
      marketingAllowed: window.marketingAllowed,
      preferencesAllowed: window.preferencesAllowed,
    };
  };

  const initialize = () => {
    registerHelpers();

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      (error) => {
        if (error) return;
        refreshFromApi('init');
      }
    );
  };

  document.addEventListener('visitorConsentCollected', () => {
    refreshFromApi('shopify-consent-event');
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
